import cn from 'classnames';
import { useIntl } from 'react-intl';
import { ButtonUI, Tooltip } from 'sonata-react-components';

import './behavior.scss';

function getBehavior(
  ad: Record<string, any>,
  formatMessage: (message: { defaultMessage: string; id: string }) => string
) {
  if (
    ad.adOptions?.redirect?.content ||
    (ad.adOptions?.redirect?.destinationUrls && ad.adOptions.redirect.destinationUrls.length > 0)
  ) {
    return {
      action: () => {
        const redirectUrl = ad.adOptions?.redirect?.destinationUrls?.[0];
        if (redirectUrl) {
          window.open(redirectUrl, '_blank');
        }
      },
      hover: ad.adOptions?.redirect?.destinationUrls?.[0],
      icon: 'fa-kit fa-redirect',
      text: formatMessage({
        defaultMessage: 'Redirect',
        id: 'multiflight.creatives.behavior.redirect'
      })
    };
  }

  switch (ad.actionType) {
    case 'map':
      return {
        action: () => null,
        disabled: true,
        hover: ad.adOptions.shop,
        text: formatMessage({
          defaultMessage: 'Show map',
          id: 'multiflight.creatives.behavior.map'
        })
      };
    case 'calendar':
      return {
        action: () => null,
        disabled: true,
        hover: '',
        text: formatMessage({
          defaultMessage: 'Add to calendar',
          id: 'multiflight.creatives.behavior.calendar'
        })
      };
    case 'download':
      return {
        action: () => null,
        disabled: true,
        hover: `${ad.adOptions?.iosUrl} - ${ad.adOptions?.androidUrl}`,
        text: formatMessage({
          defaultMessage: 'Download an app',
          id: 'multiflight.creatives.behavior.download'
        })
      };
    case 'file':
      return {
        action: () => null,
        disabled: true,
        hover: '',
        text: formatMessage({
          defaultMessage: 'Save file',
          id: 'multiflight.creatives.behavior.file'
        })
      };
    case 'sms':
      return {
        action: () => null,
        disabled: true,
        hover: '',
        text: formatMessage({
          defaultMessage: 'Send SMS',
          id: 'multiflight.creatives.behavior.sms'
        })
      };
    case 'qr':
      return {
        action: () => null,
        disabled: true,
        hover: ad.adOptions?.qrConfig.url || ad.adOptions?.url,
        text: formatMessage({
          defaultMessage: 'Go to a website',
          id: 'multiflight.creatives.behavior.qr'
        })
      };
    case 'call':
      return {
        action: () => null,
        disabled: true,
        hover: ad.adOptions?.phone,
        text: formatMessage({
          defaultMessage: 'Call you',
          id: 'multiflight.creatives.behavior.call'
        })
      };
    case 'link':
      return {
        action: () => window.open(ad.adOptions?.url, '_blank'),
        hover: ad.adOptions?.url,
        text: formatMessage({
          defaultMessage: 'Go to a website',
          id: 'multiflight.creatives.behavior.link'
        })
      };
    case 'share':
      return {
        action: () => null,
        disabled: true,
        hover: `${ad.adOptions?.share.network}\n${ad.adOptions?.share.url}`,
        icon: 'fas fa-unlink',
        text: formatMessage({
          defaultMessage: 'Share',
          id: 'multiflight.creatives.behavior.share'
        })
      };
    case 'passbook':
    case 'register':
    case 'web':
    case 'camara':
    case 'redeem':
    case 'richmedia':
      return {
        hover: '',
        text: formatMessage({
          defaultMessage: 'Not available',
          id: 'multiflight.creatives.behavior.notAvailable'
        })
      };
    default:
      return {
        hover: '',
        text: formatMessage({
          defaultMessage: 'Not available',
          id: 'multiflight.creatives.behavior.notAvailable'
        })
      };
  }
}

export function Behavior({ ad }: { ad: Record<string, any>; creative: Record<string, any> }) {
  const { formatMessage } = useIntl();
  const behavior = getBehavior(ad, formatMessage);

  return (
    <div className="behavior">
      <div className="behavior__content">
        <div className="behavior__contentText">{behavior.text}</div>
        {behavior.hover ? (
          <Tooltip
            config={{
              arrow: true,
              component: (
                <div
                  style={{
                    color: 'unset',
                    maxWidth: '200px',
                    overflowWrap: 'break-word',
                    width: '100%'
                  }}
                >
                  {behavior.hover}
                </div>
              ),
              forcePlacement: true,
              interactions: ['hover' as any],
              placement: 'bottom',
              portal: true,
              strategy: 'absolute'
            }}
          >
            <ButtonUI
              className={cn('behavior__button', {
                'behavior__button--disabled': behavior.disabled
              })}
              icon={behavior.icon || 'fas fa-up-right-from-square'}
              iconPosition="right"
              onClick={behavior.action}
              shape="link"
              size="ultraSmall"
              type="secondary"
            />
          </Tooltip>
        ) : (
          <ButtonUI
            className={cn('behavior__button', {
              'behavior__button--disabled': behavior.disabled
            })}
            icon="fas fa-unlink"
            iconPosition="right"
            onClick={behavior.action}
            shape="link"
            size="ultraSmall"
            type="secondary"
          />
        )}
      </div>
    </div>
  );
}
